// IndexedDB, wrapped so the rest of the app never touches it directly.
// Spec 7.1 storage, 8.2 (segments live here and nowhere else).
//
// Everything works offline, which matters because you will be editing
// these numbers underground with no signal.
import {
  DEFAULT_PROFILE,
  SEED_SEGMENTS,
  clampSeconds,
  type Profile,
  type WalkSegment,
} from '../../shared/segments';

const DB_NAME = 'get-me-home';
const DB_VERSION = 1;
const SEGMENTS = 'segments';
const KEYVAL = 'keyval';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SEGMENTS)) db.createObjectStore(SEGMENTS, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(KEYVAL)) db.createObjectStore(KEYVAL);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      })
  );
}

// --- segments ---------------------------------------------------------

export async function allSegments(): Promise<WalkSegment[]> {
  return tx<WalkSegment[]>(SEGMENTS, 'readonly', (s) => s.getAll());
}

export async function putSegment(seg: WalkSegment): Promise<void> {
  // Spec 7.1: clamp on the way in, not in the UI, so every writer is safe.
  const safe: WalkSegment = {
    ...seg,
    seedSeconds: clampSeconds(seg.seedSeconds),
    overrideSeconds:
      seg.overrideSeconds === undefined ? undefined : clampSeconds(seg.overrideSeconds),
    samples: seg.samples.slice(-7).map(clampSeconds),
    lastUsedAt: new Date().toISOString(),
  };
  await tx(SEGMENTS, 'readwrite', (s) => s.put(safe));
}

/** First run seeding. Never overwrites a segment the user already has. */
export async function seedIfEmpty(): Promise<WalkSegment[]> {
  const existing = await allSegments();
  const have = new Set(existing.map((s) => s.id));
  const missing = SEED_SEGMENTS.filter((s) => !have.has(s.id));
  for (const s of missing) await putSegment(s);
  return missing.length ? allSegments() : existing;
}

// --- key/value: profile, last payload, chosen destination -------------

async function getVal<T>(key: string): Promise<T | undefined> {
  return tx<T | undefined>(KEYVAL, 'readonly', (s) => s.get(key));
}

async function setVal(key: string, value: unknown): Promise<void> {
  await tx(KEYVAL, 'readwrite', (s) => s.put(value, key));
}

export const getProfile = async (): Promise<Profile> =>
  (await getVal<Profile>('profile')) ?? DEFAULT_PROFILE;

// Spec 7.1: the pace dial is for this session only and resets at midnight.
export async function saveProfile(p: Profile): Promise<void> {
  await setVal('profile', p);
  await setVal('profileSetOn', new Date().toDateString());
}

export async function loadProfileFreshToday(): Promise<Profile> {
  const setOn = await getVal<string>('profileSetOn');
  const stored = await getProfile();
  if (setOn !== new Date().toDateString()) {
    return { ...stored, paceFactor: DEFAULT_PROFILE.paceFactor };
  }
  return stored;
}

// Spec 7.2: always keep the last successful payload under a fixed key so
// nothing can evict it. A twelve minute old plan is genuinely useful. A
// spinner is not.
export const saveLastPayload = (p: unknown) =>
  setVal('lastPayload', { at: Date.now(), payload: p });

export const loadLastPayload = () =>
  getVal<{ at: number; payload: unknown }>('lastPayload');

// Spec 5.1: last known good position, for when GPS is stale underground.
export const saveLastFix = (fix: { lat: number; lon: number; at: number }) =>
  setVal('lastFix', fix);

export const loadLastFix = () => getVal<{ lat: number; lon: number; at: number }>('lastFix');
